import React, {FC, useCallback, useContext, useState} from 'react';
import cn from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {RouteName} from '@/app/constants/routing';
import {LocationType} from '@/common/constants/locationType';
import {LocationPoint} from '@/common/interfaces/location';
import {useBooleanState} from '@/common/helpers/hooks/useBooleanState';
import {useUpdateEffect} from '@/common/helpers/hooks/useUpdateEffect';
import {useLocalizer} from '@/common/services/localizer';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {StoreThunkDispatch} from '@/app/store';
import {updatePreferencesAction} from '@/app/store/config/actions';
import {selectDeviceGeolocationSettlement} from '@/app/store/geolocation/selectors';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import {ContainedButton} from '@/common/components/ContainedButton';
import {LocationSearch} from '@/common/components/LocationSearch';
import {Popup} from '@/common/components/Popup';
import {TextButton} from '@/common/components/TextButton';
import styles from './GeolocationMismatchConfirm.css';

const searchSuggestCountLimit = 20;
const allowedLocationTypes = [LocationType.Settlement];

interface GeolocationMismatchConfirmProps {
	className?: string;
	onClose(): void;
	onRefused(): void;
}

export const GeolocationMismatchConfirm: FC<GeolocationMismatchConfirmProps> = props => {
	const {className, onClose, onRefused} = props;

	const {backendAPI} = useContext(ServicesContext)!;
	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();
	const determinedLocation = useSelector(selectDeviceGeolocationSettlement);
	const t = useLocalizer();
	const dispatch = useDispatch<StoreThunkDispatch>();

	const [changing, toChanging, closeChanging] = useBooleanState(false);
	const [selectedLocation, setSelectedLocation] = useState(determinedLocation);
	const [submitting, setSubmitting] = useState(false);

	const handleLocationSelect = useCallback((location: LocationPoint) => {
		setSelectedLocation(location);
		closeChanging();
	}, []);

	const handleSearch = useCallback(
		(searchString: string) => {
			return backendAPI.search.getSearchSuggests(
				searchString,
				t.language,
				searchSuggestCountLimit,
				allowedLocationTypes,
			);
		},
		[t.language],
	);

	const handleConfirm = async () => {
		// Navigate to the determined location
		if (selectedLocation?.id === determinedLocation?.id) {
			history!.push(urlBuilder(RouteName.weather, {location: determinedLocation!}));
			onClose();
			return;
		}

		// WHen user changes location save it as default and navigateto it
		setSubmitting(true);

		try {
			await dispatch(
				updatePreferencesAction({
					mainPageLocationMode: 'defined',
					defaultLocationId: selectedLocation!.id,
					defaultLocationInfo: selectedLocation!,
				}),
			);
		} catch (error) {
			setSubmitting(false);
			throw error;
		}

		history!.push(urlBuilder(RouteName.weather, {location: selectedLocation!}));
		onClose();
	};

	// Update the selected location by changing the language
	useUpdateEffect(() => {
		if (!selectedLocation) {
			return;
		}

		backendAPI.location
			.getByIds([selectedLocation.id], t.language, ['title'])
			.then(data => setSelectedLocation(data[0]));
	}, [t.language]);

	return (
		<Popup className={cn(styles.root, className)} title={t('locationMismatchTitle')} onClose={onRefused}>
			<p className={styles.text}>{t('confirmLocationMismatch')}</p>

			{changing ? (
				<div className={styles.search}>
					<LocationSearch
						className={styles.searchInput}
						placeholder={selectedLocation?.title}
						fetchSuggest={handleSearch}
						onSelect={handleLocationSelect}
					/>
					<TextButton className={styles.searchControl} onClick={closeChanging}>
						{t('cancel')}
					</TextButton>
				</div>
			) : (
				<div className={styles.location}>
					<p className={styles.locationTitle}>{selectedLocation?.title}</p>
					<TextButton className={styles.locationControl} onClick={toChanging}>
						{t('change')}
					</TextButton>
				</div>
			)}

			<ContainedButton
				className={styles.saveControl}
				loading={submitting}
				disabled={submitting || changing}
				onClick={handleConfirm}>
				{t('yes')}
			</ContainedButton>
		</Popup>
	);
};

GeolocationMismatchConfirm.displayName = 'GeolocationMismatchConfirm';
